import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Check, ChevronDown, ChevronUp } from 'lucide-react';

import { SignaturePad, type SignaturePadValue } from '@/components/SignaturePad';
import { Modal } from '@/components/ui/Modal';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  getTechCurrentAssignment,
  startTechPresentation,
  updateTechPresentation,
  type TechCurrentAssignment,
  type TechPresentationSession,
} from '@/lib/tech-api-client';
import { formatAmount } from '@/lib/stax';
import {
  getPlanDetailsByCompany,
  type PlanDetailData,
} from '@/lib/plans/plan-details';
import {
  getDefaultPresentationPricebook,
  type PresentationPricebookItem,
  type PresentationUrgency,
} from '@/lib/tech-presentation-pricebook';

interface PublicConfigCompany {
  id: string | null;
  brand_key: string | null;
  domain?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  terms_url?: string | null;
  enrollment_config?: unknown;
}

interface MembershipPlan {
  id: string;
  plan_code: string;
  plan_name: string;
  description?: string | null;
  base_price_monthly_1_pm: number;
  base_price_annual_1_pm: number;
  base_price_monthly_2_pm: number;
  base_price_annual_2_pm: number;
  additional_system_monthly_1_pm: number;
  additional_system_annual_1_pm: number;
  additional_system_monthly_2_pm: number;
  additional_system_annual_2_pm: number;
  features?: string[] | null;
}

type PublicConfigResponse = {
  company: PublicConfigCompany;
  membership_plans: MembershipPlan[];
  discount_codes: unknown[];
};

type BillingFrequency = 'monthly' | 'annual';

type Mode = 'tech_setup' | 'customer';

type CustomerStep = 1 | 2 | 3 | 4 | 5 | 6;

type IntakeRepairItem = {
  id: string;
  code: string;
  category: PresentationPricebookItem['category'];
  customerName: string;
  technicalName?: string;
  basePriceCents: number;
  urgency: PresentationUrgency;
  doNow: boolean;
  includeInBandAid: boolean;
  selected: boolean;
};

type PresentationDecisionId = 'best_value' | 'standard' | 'band_aid' | 'no_membership' | 'decline';

type PresentationDecision = {
  id: PresentationDecisionId;
  title: string;
  subtitle: string;
  items: Array<{ label: string; priceCents: number | null }>;
  totalTodayCents: number;
  recurringMonthlyCents: number | null;
};

const REPAIR_DISCOUNT_RATE = 0.2;

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function isPlanCodeLike(plan: MembershipPlan, needle: string): boolean {
  const code = String(plan.plan_code || '').toLowerCase();
  const name = String(plan.plan_name || '').toLowerCase();
  return code.includes(needle) || name.includes(needle);
}

function getPlanRank(plan: MembershipPlan): number {
  // In this repo: "standard" is Comfort Club, "premier" is Premier, "optimum" is Optimum.
  // IMPORTANT: higher-tier plan names include "comfort", so check higher ranks first.
  if (isPlanCodeLike(plan, 'optimum') || isPlanCodeLike(plan, 'ultimate')) return 3;
  if (isPlanCodeLike(plan, 'premier') || isPlanCodeLike(plan, 'premium')) return 2;
  if (isPlanCodeLike(plan, 'standard') || isPlanCodeLike(plan, 'comfort')) return 1;
  return 50;
}

function planDiagnosticFeeCents(rank: number, nonMemberDiagnosticCents: number): number {
  // Spec examples: Standard = $69 diagnostic, Premier/Optimum = $0.
  // For the presentation we treat higher tiers as waived; "Standard" uses 69.
  if (rank <= 1) return 6900;
  return 0;
}

function calculateBasePrice(
  plan: MembershipPlan,
  billingFrequency: BillingFrequency,
  pmFrequency: 1 | 2,
  systemsCount: number
): number {
  let basePrice: number;

  if (pmFrequency === 1) {
    basePrice = billingFrequency === 'monthly' ? plan.base_price_monthly_1_pm : plan.base_price_annual_1_pm;
  } else {
    basePrice = billingFrequency === 'monthly' ? plan.base_price_monthly_2_pm : plan.base_price_annual_2_pm;
  }

  if (systemsCount > 1) {
    let additionalPrice: number;
    if (pmFrequency === 1) {
      additionalPrice = billingFrequency === 'monthly' ? plan.additional_system_monthly_1_pm : plan.additional_system_annual_1_pm;
    } else {
      additionalPrice = billingFrequency === 'monthly' ? plan.additional_system_monthly_2_pm : plan.additional_system_annual_2_pm;
    }

    basePrice += additionalPrice * (systemsCount - 1);
  }

  return basePrice;
}

function StepDots({ active }: { active: CustomerStep }) {
  const steps: CustomerStep[] = [1, 2, 3, 4, 5, 6];
  return (
    <div className="flex items-center justify-center gap-3">
      {steps.map((s) => {
        const isActive = s === active;
        const isComplete = s < active;
        return (
          <div key={s} className="flex items-center gap-3">
            <div
              className={[
                'h-4 w-4 rounded-full border-2 transition-colors',
                isActive
                  ? 'bg-blue-600 border-blue-600'
                  : isComplete
                    ? 'bg-emerald-500 border-emerald-500'
                    : 'bg-white border-gray-300',
              ].join(' ')}
              title={`Step ${s}`}
            />
            {s < 6 ? <div className={['h-[2px] w-10', isComplete ? 'bg-emerald-400' : 'bg-gray-200'].join(' ')} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function Badge({ text }: { text: string }) {
  return <span className="inline-flex items-center rounded-full bg-amber-500 px-3 py-1 text-xs font-black text-white">{text}</span>;
}

function moneyInputToCents(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function centsToMoneyInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

function formatEasternTime(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function TechPresentationPage() {
  const { accessToken, technician } = useTechAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('tech_setup');
  const [step, setStep] = useState<CustomerStep>(1);

  const [includeSavings, setIncludeSavings] = useState(true);

  const [currentAssignment, setCurrentAssignment] = useState<TechCurrentAssignment | null>(null);
  const [currentAssignmentError, setCurrentAssignmentError] = useState<string | null>(null);
  const [currentAssignmentReady, setCurrentAssignmentReady] = useState(false);

  // Membership configuration (kept minimal but compatible with enroll handoff)
  const [systemsCount, setSystemsCount] = useState(1);
  const [pmFrequency, setPmFrequency] = useState<1 | 2>(2);
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>('monthly');
  const [planId, setPlanId] = useState('');
  const [discountCode, setDiscountCode] = useState('');

  // Intake: system snapshot basics
  const [healthScore, setHealthScore] = useState(6);
  const [equipmentSummary, setEquipmentSummary] = useState('');
  const [equipmentAgeYears, setEquipmentAgeYears] = useState<number>(9);

  const [photos, setPhotos] = useState<Array<{ id: string; label: string; dataUrl: string }>>([]);

  // Pricebook selections
  const [repairs, setRepairs] = useState<IntakeRepairItem[]>(() => {
    const pb = getDefaultPresentationPricebook();
    return pb
      .filter((i) => i.category !== 'custom')
      .map((i) => ({
        id: i.id,
        code: i.code,
        category: i.category,
        customerName: i.customer_name,
        technicalName: i.technical_name,
        basePriceCents: i.default_price_cents,
        urgency: i.default_urgency,
        doNow: i.default_do_now,
        includeInBandAid: i.default_include_in_band_aid,
        selected: i.category !== 'iaq',
      }));
  });

  const [customItems, setCustomItems] = useState<IntakeRepairItem[]>([]);

  // Step 6 decision
  const [otherOptionsOpen, setOtherOptionsOpen] = useState(false);
  const [decision, setDecision] = useState<PresentationDecision | null>(null);
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [signature, setSignature] = useState<SignaturePadValue>({ dataUrl: '', isEmpty: true });
  const [signedName, setSignedName] = useState('');

  // Tracking
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [session, setSession] = useState<TechPresentationSession | null>(null);
  const [stepsCompleted, setStepsCompleted] = useState<string[]>([]);

  const domain = typeof window !== 'undefined' ? window.location.host : '';

  // Current assignment
  useEffect(() => {
    if (!accessToken) return;

    setCurrentAssignmentReady(false);
    setCurrentAssignmentError(null);

    getTechCurrentAssignment(accessToken)
      .then((res) => {
        const assignment = res.current_assignment ?? null;
        setCurrentAssignment(assignment);

        const warnings = Array.isArray(res.warnings) ? res.warnings : [];
        const missingEmployeeCode = warnings.find((w) =>
          String(w).toLowerCase().includes('successware_employee_code')
        );
        if (missingEmployeeCode) {
          setCurrentAssignmentError('Missing SuccessWare employee code (cannot auto-detect current job).');
          return;
        }

        if (!assignment && warnings.length) {
          setCurrentAssignmentError(String(warnings[0]));
        }
      })
      .catch((e) => {
        setCurrentAssignment(null);
        const msg = getErrorMessage(e);
        const lower = msg.toLowerCase();
        if (lower.includes('missing successware_employee_code') || lower.includes('successware_employee_code')) {
          setCurrentAssignmentError('Missing SuccessWare employee code (cannot auto-detect current job).');
        } else {
          setCurrentAssignmentError(msg);
        }
      })
      .finally(() => setCurrentAssignmentReady(true));
  }, [accessToken]);

  // Public config
  const {
    data: publicConfig,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ['tech-public-config', domain],
    enabled: Boolean(domain),
    queryFn: async () => {
      const resp = await fetch(`/api/memberships/public-config?domain=${encodeURIComponent(domain)}`);
      let payload: unknown = null;
      try {
        payload = await resp.json();
      } catch {
        payload = null;
      }

      if (!resp.ok || !payload || typeof payload !== 'object') {
        const obj = payload as Record<string, unknown> | null;
        const msg = obj && typeof obj.error === 'string' && obj.error.trim() ? obj.error : `Failed to load config (${resp.status})`;
        throw new Error(msg);
      }

      return payload as PublicConfigResponse;
    },
    staleTime: 60_000,
  });

  const companyId = publicConfig?.company?.id || '';
  const membershipPlans = publicConfig?.membership_plans || [];

  const sortedPlans = useMemo(() => {
    return [...membershipPlans].sort((a, b) => {
      const ra = getPlanRank(a);
      const rb = getPlanRank(b);
      if (ra !== rb) return ra - rb;
      return String(a.plan_name || '').localeCompare(String(b.plan_name || ''));
    });
  }, [membershipPlans]);

  const preferredPlanId = useMemo(() => {
    // Spec positions Premier as the default highlight.
    const premier = sortedPlans.find((p) => getPlanRank(p) === 2);
    if (premier) return premier.id;
    return sortedPlans[0]?.id || '';
  }, [sortedPlans]);

  useEffect(() => {
    if (!planId && preferredPlanId) setPlanId(preferredPlanId);
  }, [planId, preferredPlanId]);

  const selectedPlan = useMemo(() => membershipPlans.find((p) => p.id === planId) || null, [membershipPlans, planId]);
  const selectedRank = selectedPlan ? getPlanRank(selectedPlan) : 50;

  const standardPlan = useMemo(() => sortedPlans.find((p) => getPlanRank(p) === 1) || null, [sortedPlans]);

  const hasUpgradedPlans = useMemo(() => sortedPlans.some((p) => getPlanRank(p) >= 2), [sortedPlans]);

  const planDetailsMap = useMemo(() => {
    const details = getPlanDetailsByCompany(companyId);
    const map: Record<string, PlanDetailData> = {};
    for (const d of details) {
      map[d.code] = d;
    }
    return map;
  }, [companyId]);

  // Start + track presentation session (best-effort)
  useEffect(() => {
    const start = async () => {
      if (!accessToken) return;
      if (session) return;
      if (!currentAssignmentReady) return;

      setTrackingError(null);

      try {
        const deviceInfo: Record<string, unknown> = {
          presentation_spec: 'comfort_club_ipad_v1',
        };

        if (typeof navigator !== 'undefined') {
          deviceInfo.user_agent = navigator.userAgent;
        }

        if (typeof window !== 'undefined') {
          deviceInfo.viewport = { width: window.innerWidth, height: window.innerHeight };
        }

        if (currentAssignment?.source) deviceInfo.current_assignment_source = currentAssignment.source;
        if (currentAssignment?.kind) deviceInfo.current_assignment_kind = currentAssignment.kind;
        if (currentAssignment?.job?.id) deviceInfo.job_id = currentAssignment.job.id;
        if (currentAssignment?.job?.number) deviceInfo.job_number = currentAssignment.job.number;
        if (currentAssignment?.timestamps?.scheduled_for) {
          deviceInfo.scheduled_for = currentAssignment.timestamps.scheduled_for;
        }

        const created = await startTechPresentation(accessToken, {
          location_id: currentAssignment?.location?.id ?? null,
          device_info: deviceInfo,
        });

        setSession(created);
        setStepsCompleted(Array.isArray(created.steps_completed) ? created.steps_completed : []);
      } catch (e) {
        const raw = getErrorMessage(e);
        const base = 'Session tracking unavailable — you can continue, but this presentation may not be recorded.';
        if (raw && raw !== 'Error') {
          setTrackingError(`${base} (${raw})`);
        } else {
          setTrackingError(base);
        }
      }
    };

    void start();
  }, [accessToken, currentAssignment?.job?.id, currentAssignment?.job?.number, currentAssignment?.location?.id, currentAssignment?.source, currentAssignmentReady, session]);

  const stepKeyByCustomerStep: Record<CustomerStep, string> = {
    1: 'step_1_system_snapshot',
    2: 'step_2_risk_audit',
    3: 'step_3_cost_reality',
    4: 'step_4_membership_reveal',
    5: 'step_5_savings_calculator',
    6: 'step_6_decision_time',
  };

  const markStepComplete = (key: string) => {
    if (!accessToken || !session?.id) return;

    setStepsCompleted((prev) => {
      if (prev.includes(key)) return prev;
      const next = [...prev, key];

      updateTechPresentation(accessToken, session.id, {
        steps_completed: next,
      }).catch(() => {
        // no-op
      });

      return next;
    });
  };

  useEffect(() => {
    if (mode === 'customer') {
      markStepComplete(stepKeyByCustomerStep[step]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, step, session?.id, accessToken]);

  // Tech-entered price sets
  const selectedRepairItems = useMemo(() => {
    return [...repairs, ...customItems].filter((r) => r.selected && r.category !== 'diagnostic' && r.category !== 'tune_up');
  }, [repairs, customItems]);

  const diagnosticItem = useMemo(() => {
    return repairs.find((r) => r.category === 'diagnostic') || null;
  }, [repairs]);

  const tuneUpItem = useMemo(() => {
    return repairs.find((r) => r.category === 'tune_up') || null;
  }, [repairs]);

  const nonMemberDiagnosticCents = diagnosticItem?.selected ? diagnosticItem.basePriceCents : 0;
  const nonMemberTuneUpCents = tuneUpItem?.selected ? tuneUpItem.basePriceCents : 0;

  const bandAidRepairCents = useMemo(() => {
    return selectedRepairItems
      .filter((i) => i.includeInBandAid)
      .reduce((sum, i) => sum + i.basePriceCents, 0);
  }, [selectedRepairItems]);

  const recommendedRepairCents = useMemo(() => {
    return selectedRepairItems
      .filter((i) => i.doNow)
      .reduce((sum, i) => sum + i.basePriceCents, 0);
  }, [selectedRepairItems]);

  const memberMonthlyCents = useMemo(() => {
    if (!selectedPlan) return 0;
    return calculateBasePrice(selectedPlan, 'monthly', pmFrequency, systemsCount);
  }, [pmFrequency, selectedPlan, systemsCount]);

  const memberFirstPaymentCents = useMemo(() => {
    if (!selectedPlan) return 0;
    return calculateBasePrice(selectedPlan, billingFrequency, pmFrequency, systemsCount);
  }, [billingFrequency, pmFrequency, selectedPlan, systemsCount]);

  const memberDiagnosticCents = useMemo(() => {
    if (!selectedPlan) return nonMemberDiagnosticCents;
    return includeSavings ? planDiagnosticFeeCents(getPlanRank(selectedPlan), nonMemberDiagnosticCents) : nonMemberDiagnosticCents;
  }, [includeSavings, nonMemberDiagnosticCents, selectedPlan]);

  const memberTuneUpCents = useMemo(() => {
    if (!tuneUpItem?.selected) return 0;
    if (!includeSavings) return nonMemberTuneUpCents;
    // Spec implies tune-up included with membership.
    return 0;
  }, [includeSavings, nonMemberTuneUpCents, tuneUpItem?.selected]);

  const memberRecommendedRepairCents = useMemo(() => {
    if (!includeSavings) return recommendedRepairCents;
    return Math.round(recommendedRepairCents * (1 - REPAIR_DISCOUNT_RATE));
  }, [includeSavings, recommendedRepairCents]);

  const totals = useMemo(() => {
    const nonMemberBandAidTotal = nonMemberDiagnosticCents + nonMemberTuneUpCents + bandAidRepairCents;
    const nonMemberRecommendedTotal = nonMemberDiagnosticCents + nonMemberTuneUpCents + recommendedRepairCents;

    // Spec Step 5/6 uses: discounted repairs + included tune-up + waived/discounted diagnostic + membership first payment.
    const memberTotal = memberDiagnosticCents + memberTuneUpCents + memberRecommendedRepairCents + memberFirstPaymentCents;

    return {
      nonMemberBandAidTotal,
      nonMemberRecommendedTotal,
      memberTotal,
    };
  }, [bandAidRepairCents, memberDiagnosticCents, memberFirstPaymentCents, memberRecommendedRepairCents, memberTuneUpCents, nonMemberDiagnosticCents, nonMemberTuneUpCents, recommendedRepairCents]);

  const standardOptionPreview = useMemo(() => {
    if (!standardPlan) return null;

    const rank = getPlanRank(standardPlan);
    const monthly = calculateBasePrice(standardPlan, 'monthly', pmFrequency, systemsCount);
    const first = calculateBasePrice(standardPlan, billingFrequency, pmFrequency, systemsCount);
    const diag = includeSavings ? planDiagnosticFeeCents(rank, nonMemberDiagnosticCents) : nonMemberDiagnosticCents;
    const tune = tuneUpItem?.selected ? (includeSavings ? 0 : nonMemberTuneUpCents) : 0;
    const repairsCents = includeSavings ? Math.round(recommendedRepairCents * (1 - REPAIR_DISCOUNT_RATE)) : recommendedRepairCents;
    const totalTodayCents = diag + tune + repairsCents + first;

    return { monthly, totalTodayCents };
  }, [
    billingFrequency,
    includeSavings,
    nonMemberDiagnosticCents,
    nonMemberTuneUpCents,
    pmFrequency,
    recommendedRepairCents,
    standardPlan,
    systemsCount,
    tuneUpItem?.selected,
  ]);

  // Step 3 membership prompt timing
  const [membershipPromptShown, setMembershipPromptShown] = useState(false);
  useEffect(() => {
    if (mode !== 'customer' || step !== 3) {
      setMembershipPromptShown(false);
      return;
    }

    const t = window.setTimeout(() => setMembershipPromptShown(true), 2500);
    return () => window.clearTimeout(t);
  }, [mode, step]);

  const touchStartX = useRef<number | null>(null);

  const goNext = () => {
    if (mode === 'tech_setup') {
      setMode('customer');
      setStep(1);
      return;
    }

    setStep((prev) => (prev < 6 ? ((prev + 1) as CustomerStep) : prev));
  };

  const goBack = () => {
    if (mode === 'customer' && step === 1) {
      setMode('tech_setup');
      return;
    }

    if (mode === 'customer') {
      setStep((prev) => (prev > 1 ? ((prev - 1) as CustomerStep) : prev));
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    const tag = String(target?.tagName || '').toLowerCase();
    if (['input', 'select', 'textarea', 'button', 'canvas'].includes(tag)) return;
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    touchStartX.current = null;
    if (startX === null) return;

    const target = e.target as HTMLElement;
    const tag = String(target?.tagName || '').toLowerCase();
    if (['input', 'select', 'textarea', 'button', 'canvas'].includes(tag)) return;

    const endX = e.changedTouches[0]?.clientX ?? null;
    if (endX === null) return;

    const delta = endX - startX;
    if (Math.abs(delta) < 80) return;

    if (delta < 0) goNext();
    else goBack();
  };

  const handleAddPhoto = async (file: File) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const reader = new FileReader();
    const p = new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read image'));
    });

    reader.readAsDataURL(file);
    const dataUrl = await p;

    setPhotos((prev) => [...prev, { id, label: `Photo ${prev.length + 1}`, dataUrl }]);
  };

  const handlePersistIntake = () => {
    if (!accessToken || !session?.id) return;

    const intake = {
      health_score: healthScore,
      equipment_summary: equipmentSummary,
      equipment_age_years: equipmentAgeYears,
      include_savings: includeSavings,
      systems_count: systemsCount,
      pm_frequency: pmFrequency,
      billing_frequency: billingFrequency,
      plan_id: planId,
      discount_code: discountCode.trim() || null,
      items: {
        diagnostic: diagnosticItem?.selected
          ? { code: diagnosticItem.code, price_cents: diagnosticItem.basePriceCents }
          : null,
        tune_up: tuneUpItem?.selected
          ? { code: tuneUpItem.code, price_cents: tuneUpItem.basePriceCents }
          : null,
        repairs: selectedRepairItems.map((i) => ({
          code: i.code,
          customer_name: i.customerName,
          technical_name: i.technicalName || null,
          price_cents: i.basePriceCents,
          urgency: i.urgency,
          do_now: i.doNow,
          band_aid: i.includeInBandAid,
        })),
      },
      photos: photos.map((p) => ({ label: p.label, data_url: p.dataUrl })),
    };

    updateTechPresentation(accessToken, session.id, {
      device_info: {
        ...(session.device_info && typeof session.device_info === 'object' ? (session.device_info as Record<string, unknown>) : {}),
        intake,
      },
    })
      .then((updated) => setSession(updated))
      .catch(() => {
        // no-op
      });
  };

  const handleStartEnrollment = (overridePlanId?: string) => {
    const params = new URLSearchParams();

    const planIdToUse = overridePlanId || planId;
    if (planIdToUse) params.set('planId', planIdToUse);
    params.set('billingFrequency', billingFrequency);
    params.set('pmFrequency', String(pmFrequency));
    params.set('systemsCount', String(systemsCount));
    if (discountCode.trim()) params.set('discount', discountCode.trim());

    navigate(`/enroll?${params.toString()}`);
  };

  const buildDecision = (id: PresentationDecisionId): PresentationDecision | null => {
    if (!selectedPlan) return null;

    const line = (label: string, priceCents: number | null) => ({ label, priceCents });

    if (id === 'best_value') {
      const items: PresentationDecision['items'] = [];
      for (const r of selectedRepairItems.filter((i) => i.doNow)) {
        items.push(line(r.customerName, includeSavings ? Math.round(r.basePriceCents * (1 - REPAIR_DISCOUNT_RATE)) : r.basePriceCents));
      }
      if (tuneUpItem?.selected) items.push(line(tuneUpItem.customerName, includeSavings ? 0 : tuneUpItem.basePriceCents));
      items.push(line(`${selectedPlan.plan_name} (First Payment)`, memberFirstPaymentCents));

      return {
        id,
        title: 'OPTION 1',
        subtitle: `Recommended Repair + ${selectedPlan.plan_name}`,
        items,
        totalTodayCents: totals.memberTotal,
        recurringMonthlyCents: billingFrequency === 'monthly' ? memberMonthlyCents : null,
      };
    }

    if (id === 'standard') {
      if (!standardPlan) return null;
      const rank = getPlanRank(standardPlan);
      const monthly = calculateBasePrice(standardPlan, 'monthly', pmFrequency, systemsCount);
      const first = calculateBasePrice(standardPlan, billingFrequency, pmFrequency, systemsCount);
      const diag = includeSavings ? planDiagnosticFeeCents(rank, nonMemberDiagnosticCents) : nonMemberDiagnosticCents;
      const tune = tuneUpItem?.selected ? (includeSavings ? 0 : nonMemberTuneUpCents) : 0;
      const repairsCents = includeSavings ? Math.round(recommendedRepairCents * (1 - REPAIR_DISCOUNT_RATE)) : recommendedRepairCents;
      const totalTodayCents = diag + tune + repairsCents + first;

      const items: PresentationDecision['items'] = [];
      for (const r of selectedRepairItems.filter((i) => i.doNow)) {
        items.push(line(r.customerName, includeSavings ? Math.round(r.basePriceCents * (1 - REPAIR_DISCOUNT_RATE)) : r.basePriceCents));
      }
      if (tuneUpItem?.selected) items.push(line(tuneUpItem.customerName, includeSavings ? 0 : tuneUpItem.basePriceCents));
      items.push(line(`${standardPlan.plan_name} (First Payment)`, first));

      return {
        id,
        title: 'OPTION 2',
        subtitle: `Recommended Repair + ${standardPlan.plan_name}`,
        items,
        totalTodayCents,
        recurringMonthlyCents: billingFrequency === 'monthly' ? monthly : null,
      };
    }

    if (id === 'band_aid') {
      const items: PresentationDecision['items'] = [];
      for (const r of selectedRepairItems.filter((i) => i.includeInBandAid)) {
        items.push(line(r.customerName, r.basePriceCents));
      }
      if (tuneUpItem?.selected) items.push(line(tuneUpItem.customerName, tuneUpItem.basePriceCents));

      return {
        id,
        title: 'Band-Aid Repair (No Membership)',
        subtitle: 'Minimum to get you running today',
        items,
        totalTodayCents: totals.nonMemberBandAidTotal,
        recurringMonthlyCents: null,
      };
    }

    if (id === 'no_membership') {
      const items: PresentationDecision['items'] = [];
      for (const r of selectedRepairItems.filter((i) => i.doNow)) {
        items.push(line(r.customerName, r.basePriceCents));
      }
      if (tuneUpItem?.selected) items.push(line(tuneUpItem.customerName, tuneUpItem.basePriceCents));

      return {
        id,
        title: 'Recommended Repair (No Membership)',
        subtitle: 'Fix it right without joining today',
        items,
        totalTodayCents: totals.nonMemberRecommendedTotal,
        recurringMonthlyCents: null,
      };
    }

    if (id === 'decline') {
      return {
        id,
        title: 'Decline All Repairs',
        subtitle: 'Diagnostic fee only',
        items: [line('Diagnostic', nonMemberDiagnosticCents)],
        totalTodayCents: nonMemberDiagnosticCents,
        recurringMonthlyCents: null,
      };
    }

    return null;
  };

  const openDecision = (id: PresentationDecisionId) => {
    const d = buildDecision(id);
    if (!d) return;

    setDecision(d);
    setDecisionModalOpen(true);
    setSignature({ dataUrl: '', isEmpty: true });
    setSignedName('');
  };

  const confirmDecision = async () => {
    if (!decision) return;
    if (signature.isEmpty || !signature.dataUrl) return;

    if (accessToken && session?.id) {
      try {
        const deviceInfoBase = session.device_info && typeof session.device_info === 'object'
          ? (session.device_info as Record<string, unknown>)
          : {};

        const next = await updateTechPresentation(accessToken, session.id, {
          device_info: {
            ...deviceInfoBase,
            decision: {
              decision_id: decision.id,
              title: decision.title,
              subtitle: decision.subtitle,
              total_today_cents: decision.totalTodayCents,
              recurring_monthly_cents: decision.recurringMonthlyCents,
              signed_at: new Date().toISOString(),
              signed_name: signedName.trim() || null,
              signature_data_url: signature.dataUrl,
            },
          },
        });

        setSession(next);
      } catch {
        // ignore
      }
    }

    setDecisionModalOpen(false);
    setDecision(decision);
  };

  const handleFinishNoSale = async () => {
    if (!accessToken || !session?.id) {
      navigate('/dashboard');
      return;
    }

    try {
      await updateTechPresentation(accessToken, session.id, {
        outcome: 'declined',
        completed_at: new Date().toISOString(),
      });
    } catch {
      // ignore
    }

    navigate('/dashboard');
  };

  if (configLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-5xl rounded-3xl border border-gray-200 bg-white p-6">
          <div className="text-gray-700">Loading presentation…</div>
        </div>
      </div>
    );
  }

  if (configError || !publicConfig) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-5xl rounded-3xl border border-gray-200 bg-white p-6">
          <div className="text-red-600">{getErrorMessage(configError) || 'Failed to load config.'}</div>
        </div>
      </div>
    );
  }

  const showTechControls = mode === 'tech_setup';

  return (
    <div className="min-h-screen bg-gray-50" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <div className="text-[28px] font-extrabold leading-tight text-gray-900">Comfort Club Presentation</div>
            <div className="mt-1 text-sm text-gray-600">
              {technician?.full_name ? `Tech: ${technician.full_name}` : 'Technician'}
              {publicConfig.company?.id ? ` • ${publicConfig.company.id}` : ''}
            </div>
            {currentAssignmentError ? (
              <div className="mt-1 text-sm text-amber-700">Current job lookup failed: {currentAssignmentError}</div>
            ) : currentAssignmentReady ? (
              currentAssignment ? (
                <div className="mt-1 text-sm text-gray-600">
                  {currentAssignment.kind === 'next' ? 'Next job' : 'Current job'}
                  {currentAssignment.job.number ? `: #${currentAssignment.job.number}` : ''}
                  {formatEasternTime(currentAssignment.timestamps?.scheduled_for)
                    ? ` • Scheduled ${formatEasternTime(currentAssignment.timestamps?.scheduled_for)}`
                    : ''}
                  {currentAssignment.location?.address1 ? ` • ${currentAssignment.location.address1}` : ''}
                </div>
              ) : (
                <div className="mt-1 text-sm text-gray-600">No current or upcoming job detected for today.</div>
              )
            ) : (
              <div className="mt-1 text-sm text-gray-600">Detecting current job…</div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="inline-flex min-h-[48px] items-center justify-center rounded-2xl border border-gray-300 bg-white px-5 py-3 text-base font-semibold hover:bg-gray-50"
            >
              Exit
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        {trackingError ? (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <div className="font-semibold">Tracking warning</div>
            <div className="mt-1 text-sm">{trackingError}</div>
          </div>
        ) : null}

        {mode === 'customer' ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-5">
            <StepDots active={step} />
          </div>
        ) : null}

        {/* TECH SETUP (pre-presentation intake + pricebook) */}
        {mode === 'tech_setup' ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 space-y-6">
            <div>
              <div className="text-[28px] font-extrabold leading-tight text-gray-900">Tech Intake (Before Presenting)</div>
              <div className="mt-2 text-lg text-gray-700">Build today’s options from the preset pricebook.</div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5 space-y-4">
                <div className="text-sm font-bold text-gray-600 uppercase tracking-wide">
                  1) System Snapshot
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700">Equipment summary</label>
                  <input
                    className="mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-lg"
                    value={equipmentSummary}
                    onChange={(e) => setEquipmentSummary(e.target.value)}
                    placeholder="e.g. 9-year-old Carrier split system, 3 ton"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">System age (years)</label>
                    <input
                      inputMode="numeric"
                      className="mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-lg"
                      value={String(equipmentAgeYears)}
                      onChange={(e) => setEquipmentAgeYears(Number(e.target.value || 0))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Health score (1–10)</label>
                    <div className="mt-2 flex items-center gap-3">
                      <input
                        type="range"
                        min={1}
                        max={10}
                        step={1}
                        value={healthScore}
                        onChange={(e) => setHealthScore(Number(e.target.value))}
                        className="w-full accent-blue-600"
                      />
                      <div className="w-12 text-center text-2xl font-black text-gray-900 tabular-nums">{healthScore}</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-bold text-gray-700">Photos</div>
                    <label className="inline-flex cursor-pointer items-center rounded-2xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-gray-50">
                      Add photo
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          handleAddPhoto(f).catch(() => {
                            // no-op
                          });
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>

                  {photos.length ? (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {photos.map((p) => (
                        <div key={p.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-2">
                          <img src={p.dataUrl} alt={p.label} className="h-28 w-full rounded-xl object-cover" />
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <div className="text-xs font-semibold text-gray-600 truncate">{p.label}</div>
                            <button
                              type="button"
                              className="text-xs font-semibold text-red-700 hover:underline"
                              onClick={() => setPhotos((prev) => prev.filter((x) => x.id !== p.id))}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 text-sm text-gray-600">Optional: add unit/label photos for the System Snapshot.</div>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5 space-y-4">
                <div className="text-sm font-bold text-gray-600 uppercase tracking-wide">
                  2) Pricebook Options
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-gray-900">Include Comfort Club savings in comparisons</div>
                      <div className="text-xs text-gray-600">Toggle this if you want to show raw pricing without discounts/waived fees.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIncludeSavings((v) => !v)}
                      className={[
                        'min-h-[44px] rounded-2xl px-4 py-2 text-sm font-bold transition-colors',
                        includeSavings ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-900',
                      ].join(' ')}
                    >
                      {includeSavings ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="text-sm font-bold text-gray-900">Select items</div>
                  <div className="mt-3 space-y-3">
                    {repairs
                      .filter((r) => r.category !== 'custom')
                      .map((row) => (
                        <div key={row.id} className="rounded-2xl border border-gray-200 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <label className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                className="mt-1 h-5 w-5"
                                checked={row.selected}
                                onChange={(e) =>
                                  setRepairs((prev) =>
                                    prev.map((p) => (p.id === row.id ? { ...p, selected: e.target.checked } : p))
                                  )
                                }
                              />
                              <div>
                                <div className="text-base font-bold text-gray-900">{row.customerName}</div>
                                <div className="text-xs text-gray-500">{row.code}</div>
                              </div>
                            </label>

                            <div className="text-right">
                              <div className="text-xs font-semibold text-gray-600">Price</div>
                              <input
                                inputMode="decimal"
                                className="mt-1 w-28 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900"
                                value={centsToMoneyInput(row.basePriceCents)}
                                onChange={(e) =>
                                  setRepairs((prev) =>
                                    prev.map((p) => (p.id === row.id ? { ...p, basePriceCents: moneyInputToCents(e.target.value) } : p))
                                  )
                                }
                              />
                            </div>
                          </div>

                          {row.selected && row.category === 'repair' ? (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div>
                                <div className="text-xs font-semibold text-gray-600">Urgency</div>
                                <select
                                  className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
                                  value={row.urgency}
                                  onChange={(e) => {
                                    const next = e.target.value as PresentationUrgency;
                                    setRepairs((prev) => prev.map((p) => (p.id === row.id ? { ...p, urgency: next } : p)));
                                  }}
                                >
                                  <option value="now">Needs attention now</option>
                                  <option value="soon">Recommend soon</option>
                                  <option value="later">Consider later</option>
                                </select>
                              </div>

                              <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                                <input
                                  type="checkbox"
                                  checked={row.doNow}
                                  onChange={(e) =>
                                    setRepairs((prev) => prev.map((p) => (p.id === row.id ? { ...p, doNow: e.target.checked } : p)))
                                  }
                                />
                                Include in Recommended (do now)
                              </label>

                              <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                                <input
                                  type="checkbox"
                                  checked={row.includeInBandAid}
                                  onChange={(e) =>
                                    setRepairs((prev) =>
                                      prev.map((p) => (p.id === row.id ? { ...p, includeInBandAid: e.target.checked } : p))
                                    )
                                  }
                                />
                                Include in Band-Aid option
                              </label>
                            </div>
                          ) : null}
                        </div>
                      ))}

                    <div className="rounded-2xl border border-dashed border-gray-300 p-4">
                      <div className="text-sm font-bold text-gray-900">Add custom item</div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <input
                          className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
                          placeholder="Name"
                          value={(customItems[0]?.customerName && customItems[0].id === 'custom-draft') ? customItems[0].customerName : ''}
                          onChange={(e) => {
                            const name = e.target.value;
                            setCustomItems((prev) => {
                              const rest = prev.filter((p) => p.id !== 'custom-draft');
                              return [
                                {
                                  id: 'custom-draft',
                                  code: 'CUSTOM',
                                  category: 'custom',
                                  customerName: name,
                                  technicalName: name,
                                  basePriceCents: prev.find((p) => p.id === 'custom-draft')?.basePriceCents || 0,
                                  urgency: 'later',
                                  doNow: false,
                                  includeInBandAid: false,
                                  selected: true,
                                },
                                ...rest,
                              ];
                            });
                          }}
                        />
                        <input
                          inputMode="decimal"
                          className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
                          placeholder="0.00"
                          value={centsToMoneyInput(customItems.find((p) => p.id === 'custom-draft')?.basePriceCents || 0)}
                          onChange={(e) => {
                            const cents = moneyInputToCents(e.target.value);
                            setCustomItems((prev) =>
                              prev.map((p) => (p.id === 'custom-draft' ? { ...p, basePriceCents: cents } : p))
                            );
                          }}
                        />
                        <button
                          type="button"
                          className="min-h-[44px] rounded-xl bg-gray-900 px-4 py-2 text-sm font-bold text-white"
                          onClick={() => {
                            const draft = customItems.find((p) => p.id === 'custom-draft');
                            if (!draft?.customerName.trim()) return;
                            const id = `custom-${Date.now()}`;
                            setCustomItems((prev) => {
                              const withoutDraft = prev.filter((p) => p.id !== 'custom-draft');
                              return [
                                ...withoutDraft,
                                {
                                  ...draft,
                                  id,
                                  code: `CUST-${String(withoutDraft.length + 1).padStart(3, '0')}`,
                                },
                              ];
                            });
                          }}
                        >
                          Add
                        </button>
                      </div>
                      <div className="mt-2 text-xs text-gray-600">Custom items default to “Consider later” unless you mark them as recommended.</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="text-sm font-bold text-gray-900">Membership plan (for the membership reveal + totals)</div>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {sortedPlans.slice(0, 3).map((p) => {
                      const rank = getPlanRank(p);
                      const planCode = String(p.plan_code || '').toLowerCase();
                      const details = planDetailsMap[planCode];
                      const monthly = calculateBasePrice(p, 'monthly', pmFrequency, systemsCount);
                      const isSelected = p.id === planId;
                      const isDisabled = hasUpgradedPlans && rank === 1;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            if (isDisabled) return;
                            setPlanId(p.id);
                          }}
                          disabled={isDisabled}
                          className={[
                            'rounded-2xl border p-4 text-left transition-all active:scale-[0.98]',
                            isDisabled
                              ? 'border-gray-200 bg-gray-50 opacity-60'
                              : isSelected
                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                : 'border-gray-200 bg-white hover:bg-gray-50',
                          ].join(' ')}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-base font-extrabold text-gray-900">{p.plan_name}</div>
                              <div className="mt-1 text-sm text-gray-700">{formatAmount(monthly)}/mo</div>
                              <div className="mt-1 text-xs text-gray-500">{details?.tagline || ''}</div>
                            </div>
                            {rank >= 2 ? <Badge text={rank === 3 ? 'BEST VALUE' : 'MOST POPULAR'} /> : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <div className="text-xs font-bold text-gray-600 uppercase">Systems</div>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          className="h-10 w-10 rounded-xl border border-gray-300 bg-white text-xl font-black"
                          onClick={() => setSystemsCount((v) => Math.max(1, v - 1))}
                        >
                          −
                        </button>
                        <div className="w-12 text-center text-2xl font-black text-gray-900 tabular-nums">{systemsCount}</div>
                        <button
                          type="button"
                          className="h-10 w-10 rounded-xl border border-gray-300 bg-white text-xl font-black"
                          onClick={() => setSystemsCount((v) => Math.min(5, v + 1))}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <div className="text-xs font-bold text-gray-600 uppercase">Tune-ups / Year</div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {[1, 2].map((n) => (
                          <button
                            key={n}
                            type="button"
                            className={[
                              'h-10 rounded-xl border text-sm font-bold',
                              pmFrequency === n ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-900',
                            ].join(' ')}
                            onClick={() => setPmFrequency(n as 1 | 2)}
                          >
                            {n}x
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <div className="text-xs font-bold text-gray-600 uppercase">Discount code (optional)</div>
                      <input
                        className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold"
                        placeholder="e.g. WELCOMEBACK25"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5">
                  <div className="text-xs font-bold text-emerald-800 uppercase">Live totals (based on your selections)</div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-2xl border border-emerald-200 bg-white p-4">
                      <div className="text-sm font-bold text-gray-700">Band-Aid (no membership)</div>
                      <div className="mt-2 text-3xl font-black text-gray-900">{formatAmount(totals.nonMemberBandAidTotal)}</div>
                      <div className="mt-1 text-xs text-gray-600">Diagnostic + Tune-up + minimal repair</div>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-white p-4">
                      <div className="text-sm font-bold text-gray-700">Recommended (no membership)</div>
                      <div className="mt-2 text-3xl font-black text-gray-900">{formatAmount(totals.nonMemberRecommendedTotal)}</div>
                      <div className="mt-1 text-xs text-gray-600">Diagnostic + Tune-up + recommended repairs</div>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-white p-4">
                      <div className="text-sm font-bold text-gray-700">With membership</div>
                      <div className="mt-2 text-3xl font-black text-emerald-700">{formatAmount(totals.memberTotal)}</div>
                      <div className="mt-1 text-xs text-gray-600">Includes first payment + savings (toggle above)</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-between">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                  >
                    Back to Dashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handlePersistIntake();
                      setMode('customer');
                      setStep(1);
                    }}
                    className="min-h-[56px] rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-bold text-white shadow-lg"
                  >
                    Start Customer Presentation →
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* CUSTOMER STEPS */}
        {mode === 'customer' ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            {/* STEP 1 */}
            {step === 1 ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Step 1</div>
                  <div className="mt-2 text-[32px] font-black text-gray-900">System Snapshot</div>
                  <div className="mt-2 text-lg text-gray-700">Here’s what I found after checking your system.</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
                    <div className="text-sm font-bold text-gray-600 uppercase tracking-wide">Your system</div>
                    <div className="mt-2 text-xl font-extrabold text-gray-900">{equipmentSummary || 'HVAC system'}</div>
                    <div className="mt-2 text-sm text-gray-700">Age: {equipmentAgeYears} year(s)</div>

                    <div className="mt-5 rounded-3xl border border-gray-200 bg-white p-5">
                      <div className="text-sm font-bold text-gray-600 uppercase">Health score</div>
                      <div className="mt-2 flex items-baseline justify-between">
                        <div className="text-6xl font-black text-gray-900 tabular-nums">{healthScore}</div>
                        <div className="text-lg font-semibold text-gray-700">/ 10</div>
                      </div>
                      <div className="mt-3 text-sm text-gray-600">
                        {healthScore >= 8
                          ? 'Strong system — only minor issues found.'
                          : healthScore >= 5
                            ? 'Functional, but showing wear in a few key areas.'
                            : 'High risk — needs attention soon to avoid breakdowns.'}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
                    <div className="text-sm font-bold text-gray-600 uppercase tracking-wide">Photos</div>
                    {photos.length ? (
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        {photos.slice(0, 4).map((p) => (
                          <img key={p.id} src={p.dataUrl} alt={p.label} className="h-40 w-full rounded-2xl object-cover" />
                        ))}
                      </div>
                    ) : (
                      <div className="mt-4 text-sm text-gray-600">No photos added.</div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="min-h-[56px] rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-bold text-white shadow-lg"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : null}

            {/* STEP 2 */}
            {step === 2 ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Step 2</div>
                  <div className="mt-2 text-[32px] font-black text-gray-900">Risk Audit</div>
                  <div className="mt-2 text-lg text-gray-700">I organized what I found into simple categories.</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  <div className="rounded-3xl border-2 border-red-200 bg-red-50 p-5">
                    <div className="text-sm font-black text-red-800 uppercase tracking-wide">Needs attention now</div>
                    <div className="mt-3 space-y-3">
                      {selectedRepairItems.filter((i) => i.urgency === 'now').length ? (
                        selectedRepairItems
                          .filter((i) => i.urgency === 'now')
                          .map((i) => (
                            <div key={i.code} className="rounded-2xl border border-red-200 bg-white p-4">
                              <div className="font-extrabold text-gray-900">{i.customerName}</div>
                              <div className="mt-1 text-sm text-gray-700">This is what gets you running today.</div>
                            </div>
                          ))
                      ) : (
                        <div className="rounded-2xl border border-red-200 bg-white p-4 text-sm text-gray-700">No urgent items selected.</div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-5">
                    <div className="text-sm font-black text-amber-800 uppercase tracking-wide">Recommend soon</div>
                    <div className="mt-3 space-y-3">
                      {selectedRepairItems.filter((i) => i.urgency === 'soon').length ? (
                        selectedRepairItems
                          .filter((i) => i.urgency === 'soon')
                          .map((i) => (
                            <div key={i.code} className="rounded-2xl border border-amber-200 bg-white p-4">
                              <div className="font-extrabold text-gray-900">{i.customerName}</div>
                              <div className="mt-1 text-sm text-gray-700">Not failed yet — but trending in the wrong direction.</div>
                            </div>
                          ))
                      ) : (
                        <div className="rounded-2xl border border-amber-200 bg-white p-4 text-sm text-gray-700">No recommended items selected.</div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-3xl border-2 border-blue-200 bg-blue-50 p-5">
                    <div className="text-sm font-black text-blue-800 uppercase tracking-wide">Consider later</div>
                    <div className="mt-3 space-y-3">
                      {selectedRepairItems.filter((i) => i.urgency === 'later').length ? (
                        selectedRepairItems
                          .filter((i) => i.urgency === 'later')
                          .map((i) => (
                            <div key={i.code} className="rounded-2xl border border-blue-200 bg-white p-4">
                              <div className="font-extrabold text-gray-900">{i.customerName}</div>
                              <div className="mt-1 text-sm text-gray-700">Nice upgrade — can be done later if you want.</div>
                            </div>
                          ))
                      ) : (
                        <div className="rounded-2xl border border-blue-200 bg-white p-4 text-sm text-gray-700">No later items selected.</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="min-h-[56px] rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-bold text-white shadow-lg"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : null}

            {/* STEP 3 */}
            {step === 3 ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Step 3</div>
                  <div className="mt-2 text-[32px] font-black text-gray-900">Cost Reality</div>
                  <div className="mt-2 text-lg text-gray-700">Here are your options without membership—first.</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="rounded-3xl border border-gray-200 bg-white p-6">
                    <div className="text-sm font-black text-gray-700 uppercase">Option A — Band-Aid</div>
                    <div className="mt-2 text-xl font-extrabold text-gray-900">Minimum to get you running</div>
                    <div className="mt-4 text-5xl font-black text-gray-900">{formatAmount(totals.nonMemberBandAidTotal)}</div>
                    <div className="mt-4 space-y-2 text-sm text-gray-700">
                      <div>• Diagnostic: {formatAmount(nonMemberDiagnosticCents)}</div>
                      {tuneUpItem?.selected ? <div>• Tune-up: {formatAmount(nonMemberTuneUpCents)}</div> : null}
                      {selectedRepairItems.filter((i) => i.includeInBandAid).map((i) => (
                        <div key={i.code}>• {i.customerName}: {formatAmount(i.basePriceCents)}</div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-gray-200 bg-white p-6">
                    <div className="text-sm font-black text-gray-700 uppercase">Option B — Recommended</div>
                    <div className="mt-2 text-xl font-extrabold text-gray-900">Fix it right</div>
                    <div className="mt-4 text-5xl font-black text-gray-900">{formatAmount(totals.nonMemberRecommendedTotal)}</div>
                    <div className="mt-4 space-y-2 text-sm text-gray-700">
                      <div>• Diagnostic: {formatAmount(nonMemberDiagnosticCents)}</div>
                      {tuneUpItem?.selected ? <div>• Tune-up: {formatAmount(nonMemberTuneUpCents)}</div> : null}
                      {selectedRepairItems.filter((i) => i.doNow).map((i) => (
                        <div key={i.code}>• {i.customerName}: {formatAmount(i.basePriceCents)}</div>
                      ))}
                    </div>
                  </div>
                </div>

                {membershipPromptShown ? (
                  <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-5">
                    <div className="text-lg font-extrabold text-amber-900">Quick question… are you a member?</div>
                    <div className="mt-2 text-sm text-amber-900">
                      Before you decide, let me show you how membership can change today’s total.
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={goNext}
                        className="min-h-[56px] flex-1 rounded-2xl bg-gray-900 px-6 py-4 text-lg font-bold text-white"
                      >
                        Show me membership options
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        className="min-h-[56px] flex-1 rounded-2xl border-2 border-amber-300 bg-white px-6 py-4 text-lg font-bold text-amber-900"
                      >
                        I’m not sure (show me)
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-sm text-gray-500">…</div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="min-h-[56px] rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-bold text-white shadow-lg"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : null}

            {/* STEP 4 */}
            {step === 4 ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Step 4</div>
                  <div className="mt-2 text-[32px] font-black text-gray-900">Membership Reveal</div>
                  <div className="mt-2 text-lg text-gray-700">Here’s what membership does for you today and all year.</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  {sortedPlans.slice(0, 3).map((p) => {
                    const rank = getPlanRank(p);
                    const planCode = String(p.plan_code || '').toLowerCase();
                    const details = planDetailsMap[planCode];
                    const monthly = calculateBasePrice(p, 'monthly', pmFrequency, systemsCount);
                    const selected = p.id === planId;
                    const isDisabled = hasUpgradedPlans && rank === 1;

                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          if (isDisabled) return;
                          setPlanId(p.id);
                        }}
                        disabled={isDisabled}
                        className={[
                          'rounded-3xl border p-6 text-left transition-all active:scale-[0.98]',
                          isDisabled
                            ? 'border-gray-200 bg-gray-50 opacity-60'
                            : selected
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                              : 'border-gray-200 bg-white hover:bg-gray-50',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">{details?.tagline || 'Membership'}</div>
                            <div className="mt-1 text-2xl font-black text-gray-900">{p.plan_name}</div>
                            <div className="mt-2 text-sm text-gray-700">{formatAmount(monthly)}/mo</div>
                          </div>
                          {rank >= 2 ? <Badge text={rank === 3 ? 'BEST VALUE' : 'MOST POPULAR'} /> : null}
                        </div>

                        <div className="mt-4 space-y-2 text-sm text-gray-700">
                          <div className="flex items-start gap-2">
                            <Check className="mt-0.5 h-4 w-4 text-emerald-600" />
                            <span>Discounted / waived diagnostics</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Check className="mt-0.5 h-4 w-4 text-emerald-600" />
                            <span>Tune-up included</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Check className="mt-0.5 h-4 w-4 text-emerald-600" />
                            <span>20% off repairs</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="min-h-[56px] rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-bold text-white shadow-lg"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : null}

            {/* STEP 5 */}
            {step === 5 ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Step 5</div>
                  <div className="mt-2 text-[32px] font-black text-gray-900">Personalized Savings</div>
                  <div className="mt-2 text-lg text-gray-700">Let’s look at your actual numbers.</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="rounded-3xl border border-gray-200 bg-white p-6">
                    <div className="text-sm font-black text-gray-700 uppercase">WITHOUT membership</div>
                    <div className="mt-4 text-6xl font-black text-gray-900">{formatAmount(totals.nonMemberRecommendedTotal)}</div>
                    <div className="mt-4 space-y-2 text-sm text-gray-700">
                      <div>• Diagnostic: {formatAmount(nonMemberDiagnosticCents)}</div>
                      {tuneUpItem?.selected ? <div>• Tune-up: {formatAmount(nonMemberTuneUpCents)}</div> : null}
                      {selectedRepairItems.filter((i) => i.doNow).map((i) => (
                        <div key={i.code}>• {i.customerName}: {formatAmount(i.basePriceCents)}</div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border-2 border-emerald-200 bg-emerald-50 p-6">
                    <div className="text-sm font-black text-emerald-800 uppercase">WITH membership</div>
                    <div className="mt-4 text-6xl font-black text-emerald-700">{formatAmount(totals.memberTotal)}</div>
                    <div className="mt-4 space-y-2 text-sm text-emerald-900">
                      <div>• Diagnostic: {formatAmount(memberDiagnosticCents)}</div>
                      {tuneUpItem?.selected ? <div>• Tune-up: {formatAmount(memberTuneUpCents)}</div> : null}
                      <div>• Repairs: {formatAmount(memberRecommendedRepairCents)}</div>
                      <div>• {selectedPlan?.plan_name} (first payment): {formatAmount(memberFirstPaymentCents)}</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-gray-200 bg-white p-6">
                  <div className="text-base font-extrabold text-gray-900">
                    The recommended repair WITH membership costs LESS than the band-aid fix WITHOUT membership.
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-sm font-bold text-gray-700">Band-Aid (no membership)</div>
                      <div className="mt-1 text-3xl font-black text-gray-900">{formatAmount(totals.nonMemberBandAidTotal)}</div>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                      <div className="text-sm font-bold text-emerald-900">Recommended + membership</div>
                      <div className="mt-1 text-3xl font-black text-emerald-700">{formatAmount(totals.memberTotal)}</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="min-h-[56px] rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-bold text-white shadow-lg"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : null}

            {/* STEP 6 */}
            {step === 6 ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Step 6</div>
                  <div className="mt-2 text-[32px] font-black text-gray-900">Decision Time</div>
                  <div className="mt-2 text-lg text-gray-700">Select your choice to move forward.</div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-black text-amber-800 uppercase">Option 1</div>
                        <div className="mt-1 text-2xl font-black text-gray-900">Recommended Repair + {selectedPlan?.plan_name}</div>
                        <div className="mt-2 text-sm text-gray-700">Best value — lowest today’s total with the most protection.</div>
                      </div>
                      <Badge text="BEST VALUE" />
                    </div>

                    <div className="mt-4 rounded-2xl border border-amber-200 bg-white p-4">
                      <div className="text-4xl font-black text-gray-900">{formatAmount(totals.memberTotal)} TODAY</div>
                      {billingFrequency === 'monthly' ? (
                        <div className="mt-1 text-sm text-gray-700">Then {formatAmount(memberMonthlyCents)}/mo</div>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      className="mt-4 w-full min-h-[56px] rounded-2xl bg-gray-900 px-8 py-4 text-lg font-black text-white"
                      onClick={() => openDecision('best_value')}
                    >
                      ✓ SELECT THIS OPTION
                    </button>
                  </div>

                  {standardPlan ? (
                    <div className="rounded-3xl border border-gray-200 bg-white p-5">
                      <div className="text-xs font-black text-gray-600 uppercase">Option 2</div>
                      <div className="mt-1 text-xl font-extrabold text-gray-900">Recommended Repair + {standardPlan.plan_name}</div>
                      <div className="mt-2 text-sm text-gray-700">Lower monthly cost, still includes membership benefits.</div>

                      <div className="mt-4 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-2xl font-black text-gray-900">
                            {standardOptionPreview ? (
                              <>
                                {formatAmount(standardOptionPreview.totalTodayCents)} TODAY
                              </>
                            ) : (
                              <>Tap to select</>
                            )}
                          </div>
                          {billingFrequency === 'monthly' && standardOptionPreview ? (
                            <div className="mt-1 text-xs text-gray-600">Then {formatAmount(standardOptionPreview.monthly)}/mo</div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="min-h-[44px] rounded-2xl border-2 border-gray-300 bg-white px-5 py-3 text-base font-bold text-gray-900"
                          onClick={() => openDecision('standard')}
                        >
                          SELECT
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="rounded-3xl border border-gray-200 bg-white p-5">
                    <button
                      type="button"
                      onClick={() => setOtherOptionsOpen((v) => !v)}
                      className="flex w-full items-center justify-between gap-3"
                    >
                      <div className="text-sm font-black text-gray-700 uppercase">Other Options</div>
                      {otherOptionsOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </button>

                    {otherOptionsOpen ? (
                      <div className="mt-4 space-y-3">
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between gap-3">
                          <div>
                            <div className="font-extrabold text-gray-900">Band-Aid Repair (No Membership)</div>
                            <div className="text-sm text-gray-700">{formatAmount(totals.nonMemberBandAidTotal)}</div>
                          </div>
                          <button
                            type="button"
                            className="min-h-[44px] rounded-2xl border-2 border-gray-300 bg-white px-5 py-3 text-base font-bold text-gray-900"
                            onClick={() => openDecision('band_aid')}
                          >
                            SELECT
                          </button>
                        </div>

                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between gap-3">
                          <div>
                            <div className="font-extrabold text-gray-900">Recommended Repair (No Membership)</div>
                            <div className="text-sm text-gray-700">{formatAmount(totals.nonMemberRecommendedTotal)}</div>
                          </div>
                          <button
                            type="button"
                            className="min-h-[44px] rounded-2xl border-2 border-gray-300 bg-white px-5 py-3 text-base font-bold text-gray-900"
                            onClick={() => openDecision('no_membership')}
                          >
                            SELECT
                          </button>
                        </div>

                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between gap-3">
                          <div>
                            <div className="font-extrabold text-gray-900">Decline All Repairs</div>
                            <div className="text-sm text-gray-700">{formatAmount(nonMemberDiagnosticCents)}</div>
                          </div>
                          <button
                            type="button"
                            className="min-h-[44px] rounded-2xl border-2 border-gray-300 bg-white px-5 py-3 text-base font-bold text-gray-900"
                            onClick={() => openDecision('decline')}
                          >
                            SELECT
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                    By selecting an option, you authorize the work described above.
                  </div>

                  {decision ? (
                    <div className="rounded-3xl border-2 border-emerald-200 bg-emerald-50 p-5">
                      <div className="text-sm font-black text-emerald-800 uppercase">Selection recorded</div>
                      <div className="mt-1 text-2xl font-black text-gray-900">{decision.subtitle}</div>
                      <div className="mt-2 text-sm text-gray-700">Total today: {formatAmount(decision.totalTodayCents)}</div>

                      {decision.id === 'best_value' || decision.id === 'standard' ? (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            className="min-h-[56px] rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-4 text-lg font-black text-white"
                            onClick={() => handleStartEnrollment(decision.id === 'standard' ? standardPlan?.id : undefined)}
                          >
                            Continue to Membership Enrollment
                          </button>
                          <button
                            type="button"
                            className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                            onClick={() => navigate('/dashboard')}
                          >
                            Done
                          </button>
                        </div>
                      ) : (
                        <div className="mt-4">
                          <button
                            type="button"
                            className="w-full min-h-[56px] rounded-2xl bg-gray-900 px-8 py-4 text-lg font-black text-white"
                            onClick={handleFinishNoSale}
                          >
                            Finish
                          </button>
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className="flex flex-col sm:flex-row gap-3 justify-between">
                    <button
                      type="button"
                      onClick={goBack}
                      className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('tech_setup')}
                      className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                    >
                      Edit Tech Setup
                    </button>
                  </div>
                </div>

                <Modal
                  isOpen={decisionModalOpen}
                  onClose={() => setDecisionModalOpen(false)}
                  title="Confirm your selection"
                  maxWidthClassName="max-w-2xl"
                >
                  {decision ? (
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm font-bold text-gray-700 uppercase">{decision.title}</div>
                        <div className="mt-1 text-xl font-extrabold text-gray-900">{decision.subtitle}</div>
                        <div className="mt-4 space-y-2 text-sm text-gray-800">
                          {decision.items.map((i, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-3">
                              <span className="font-semibold">{i.label}</span>
                              <span className="font-black">{i.priceCents === null ? '—' : formatAmount(i.priceCents)}</span>
                            </div>
                          ))}
                          <div className="pt-3 mt-3 border-t border-gray-200 flex items-center justify-between text-base">
                            <span className="font-black">TOTAL TODAY</span>
                            <span className="text-2xl font-black text-gray-900">{formatAmount(decision.totalTodayCents)}</span>
                          </div>
                          {decision.recurringMonthlyCents !== null ? (
                            <div className="text-xs text-gray-600">Membership renews at {formatAmount(decision.recurringMonthlyCents)}/mo. Cancel anytime.</div>
                          ) : null}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700">Signed name (optional)</label>
                        <input
                          className="mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-lg"
                          value={signedName}
                          onChange={(e) => setSignedName(e.target.value)}
                          placeholder="Type your name"
                        />
                      </div>

                      <SignaturePad heightPx={220} onChange={setSignature} />

                      {signature.isEmpty ? (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          Signature is required to confirm.
                        </div>
                      ) : null}

                      <div className="flex flex-col sm:flex-row gap-3 justify-between">
                        <button
                          type="button"
                          onClick={() => setDecisionModalOpen(false)}
                          className="min-h-[56px] rounded-2xl border-2 border-gray-300 bg-white px-8 py-4 text-lg font-bold text-gray-900"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={signature.isEmpty}
                          onClick={() => {
                            void confirmDecision();
                          }}
                          className={[
                            'min-h-[56px] rounded-2xl px-8 py-4 text-lg font-black text-white',
                            signature.isEmpty
                              ? 'bg-gray-300'
                              : 'bg-gradient-to-r from-emerald-500 to-teal-600',
                          ].join(' ')}
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700">Missing selection.</div>
                  )}
                </Modal>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* NOTE: when we are in customer mode, we intentionally hide most tech-only controls. */}
        {!showTechControls ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-4 text-xs text-gray-500">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4" />
              <div>
                Swipe left/right to navigate steps. (Tech: tap “Edit Tech Setup” in Step 6 to modify options.)
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
